import { useState, useEffect } from "react";
import { Search, ChevronUp, AlertTriangle, Info, ClipboardCheck, Download, Printer, CheckCircle, XCircle, Clock, Users, HelpCircle, Tablet, Bus as BusIcon } from "lucide-react";
import SopSwitcher from "./SopSwitcher";
import SopImage from "./SopImage";
import "../styles/tutorialPrint.css";

function Why({ children }) {
  return (
    <div className="sop-why sop-block bg-indigo-50 border-l-4 border-indigo-600 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-indigo-900 mb-1 flex items-center gap-1"><Info className="w-4 h-4" /> Why does this matter?</p>
      <p className="text-sm text-indigo-800">{children}</p>
    </div>
  );
}

function Mistakes({ items }) {
  return (
    <div className="sop-mistakes sop-block bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> DO NOT do these things</p>
      <ul className="list-disc pl-5 space-y-1">
        {items.map((item, i) => <li key={i} className="text-sm text-amber-800">{item}</li>)}
      </ul>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="sop-steps sop-block list-decimal pl-6 space-y-2 my-3">
      {items.map((item, i) => <li key={i} className="text-base text-gray-700 leading-relaxed">{item}</li>)}
    </ol>
  );
}

function Rule({ children }) {
  return (
    <div className="sop-block p-4 bg-red-50 border-2 border-red-500 rounded-xl my-4">
      <p className="font-bold text-red-900">{children}</p>
    </div>
  );
}

const SECTIONS = [
  { id: "what-is-attendance", num: "1", title: "What Is the Attendance App?" },
  { id: "the-home-screen", num: "2", title: "The Home Screen — Pick the Right Option" },
  { id: "summer-login", num: "3", title: "Summer 2026 — Group Login (Counselors)" },
  { id: "marking-attendance", num: "4", title: "How to Mark Attendance" },
  { id: "deadline-group", num: "5", title: "The 10:00 AM Deadline" },
  { id: "front-desk", num: "6", title: "Front Desk — Late Arrivals & Early Dismissals" },
  { id: "bus-attendance", num: "7", title: "Bus Attendance (Read-Only View)" },
  { id: "how-it-connects", num: "8", title: "How It All Connects to Camper Snapshot" },
  { id: "troubleshooting", num: "9", title: "Something Not Working?" },
];

export default function TutorialAttendance() {
  const [search, setSearch] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const filtered = search ? SECTIONS.filter(s => s.title.toLowerCase().includes(search.toLowerCase())) : SECTIONS;

  return (
    <div className="min-h-screen bg-white" data-testid="tutorial-attendance-page">
      <header className="sticky top-0 z-50 bg-indigo-800 text-white shadow-lg print:static print:bg-white print:text-black">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Rolling River Day Camp</h1>
              <p className="text-xs text-indigo-200">Attendance App Tutorial</p>
            </div>
            <div className="hidden sm:block">
              <SopSwitcher current="/tutorialattendance" accent="indigo" />
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <div className="sm:hidden">
              <SopSwitcher current="/tutorialattendance" accent="indigo" />
            </div>
            <button onClick={() => window.print()} className="text-xs bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 lg:flex lg:gap-8">
        <nav aria-label="TOC" className="lg:w-64 lg:flex-shrink-0 mb-6 lg:mb-0 print:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-full text-left bg-gray-50 border rounded-xl p-3 text-sm font-bold text-gray-700 flex items-center justify-between" data-testid="toc-toggle">
            Table of Contents <ChevronUp className={`w-4 h-4 transition-transform ${menuOpen ? "" : "rotate-180"}`} />
          </button>
          <div className={`lg:sticky lg:top-20 ${menuOpen ? "block" : "hidden lg:block"} mt-2 lg:mt-0`}>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" placeholder="Search sections..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400" data-testid="toc-search" />
            </div>
            <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filtered.map(s => (
                <li key={s.id}><a href={`#${s.id}`} onClick={() => setMenuOpen(false)} className="block px-3 py-1.5 text-sm rounded-lg hover:bg-indigo-50 text-gray-600">{s.num}. {s.title}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          <section className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Attendance App — How to Use It</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">This is where you mark your group's daily attendance. Log in with your Group Code and Group Name. Mark each camper Present or Absent.</p>
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-5 mb-5">
              <h3 className="text-lg font-bold text-red-900 mb-2">Daily Morning Deadlines</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-red-800">
                <li><strong>Buddy Sheets</strong>: <strong>9:20 AM</strong></li>
                <li><strong>Bus Attendance</strong> (rrdcbusmapping.com): <strong>9:30 AM</strong></li>
                <li><strong>Lunch Orders</strong> (rrdcdining.com): <strong>9:30 AM</strong></li>
                <li><strong>Group Attendance</strong> (this app): <strong>10:00 AM</strong></li>
              </ul>
            </div>
            <p className="text-xs text-gray-400">Website: rrdcattendance.com</p>
          </section>

          {/* 1 */}
          <div id="what-is-attendance" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><ClipboardCheck className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">1. What Is the Attendance App?</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Attendance App (<strong>rrdcattendance.com</strong>) is the master attendance system for Rolling River Day Camp. It has four sections:</p>
            <div className="space-y-2">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg"><strong className="text-sm text-yellow-900">Holiday Camp</strong> — <span className="text-sm text-yellow-700">For spring/holiday camp only. Not used during summer.</span></div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg"><strong className="text-sm text-blue-900">Summer 2026</strong> — <span className="text-sm text-blue-700">This is YOUR section. Jun 29 - Aug 20. Log in with your Group Code + Group Name to mark attendance.</span></div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg"><strong className="text-sm text-green-900">Front Desk</strong> — <span className="text-sm text-green-700">Tablet at the front desk for marking late arrivals and early dismissals. Counselors do NOT use this. Front desk staff only.</span></div>
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg"><strong className="text-sm text-purple-900">Bus Attendance</strong> — <span className="text-sm text-purple-700">Read-only view of bus check-in data from rrdcbusmapping.com. You don't need to open this.</span></div>
            </div>
            <Why>As a counselor, you ONLY use the Summer 2026 section. Ignore the other three.</Why>
          </div>

          {/* 2 */}
          <div id="the-home-screen" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Tablet className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">2. The Home Screen — Pick the Right Option</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <SopImage filename="attendance_program_select.png" caption="rrdcattendance.com home — pick the Summer 2026 card." />
            <Steps items={[
              <span key="1">Open your browser. Go to <strong>rrdcattendance.com</strong>.</span>,
              <span key="2">You see the home screen with four big cards: Holiday Camp, Summer 2026, Front Desk, Bus Attendance.</span>,
              <span key="3">Tap <strong>Summer 2026</strong>.</span>,
              <span key="4">You are taken to the Group Login screen.</span>,
            ]} />
            <Mistakes items={[
              "Tapping 'Front Desk' — that's for the front desk tablet, not for you.",
              "Tapping 'Holiday Camp' — that's for spring camp, not summer.",
              "Tapping 'Bus Attendance' — that's a read-only view, you can't mark attendance there.",
            ]} />
          </div>

          {/* 3 */}
          <div id="summer-login" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Users className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">3. Summer 2026 — Group Login (Counselors)</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <Rule>Open the Attendance App from the <strong>"Group Attendance"</strong> button on your Camper Snapshot dashboard. That's the only way you should be opening it.</Rule>
            <SopImage filename="attendance_login.png" caption="Group login screen — username = group code (B1), password = group name (Lobsters)." />
            <p className="text-base text-gray-700">Tapping Group Attendance takes you to the Summer 2026 login screen:</p>
            <Steps items={[
              <span key="1"><strong>Username (Group Code)</strong> — Any format works: b1, B1, b01, B01. Same code as Camper Snapshot.</span>,
              <span key="2"><strong>Password (Group Name)</strong> — Your group name (e.g. Lobsters, Swordfish). Same as Camper Snapshot. Case-sensitive.</span>,
              <span key="3">Tap <strong>Sign In</strong>.</span>,
              <span key="4">You see your group's camper list with attendance buttons.</span>,
            ]} />
            <div className="p-3 bg-indigo-50 rounded-xl border border-indigo-200">
              <p className="text-sm text-indigo-700">If you can log in to Camper Snapshot, you can log in here with the exact same credentials.</p>
            </div>
          </div>

          {/* 4 */}
          <div id="marking-attendance" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><CheckCircle className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">4. How to Mark Attendance</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <Rule>Mark EVERY camper in your group. Present or Absent. Do not leave anyone unmarked.</Rule>
            <SopImage filename="attendance_dashboard.png" caption="After login — group cards. Tap your group to open the camper marking screen." />
            <Steps items={[
              "After logging in, you see your group's campers listed alphabetically.",
              <span key="2">For each camper who is <strong>at camp today</strong>, mark them <strong>Present</strong>.</span>,
              <span key="3">For each camper who is <strong>NOT at camp today</strong>, mark them <strong>Absent</strong>.</span>,
              "Each mark saves automatically. You do not need to tap a Submit button.",
              "When you're done, every camper should be either Present or Absent. Zero unmarked.",
              <span key="6">Immediately go back to Camper Snapshot and tap <strong>Conflict Match</strong> on your dashboard. Check for any red conflict rows. If you see one, notify your supervisor — only they can resolve it.</span>,
            ]} />
            <div className="p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg">
              <p className="text-sm text-emerald-900"><strong>New:</strong> When Conflict Match detects a red conflict for your group, the system <strong>automatically texts your Supervisor</strong> at the same time. You still tell them in person, but they will already know.</p>
            </div>
            <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-xl">
              <p className="font-bold text-amber-900 mb-2">Important: You are marking who is at CAMP, not who was on the bus.</p>
              <p className="text-sm text-amber-800">The bus app tracks who rode the bus. This app tracks who actually made it to camp. A kid might get dropped off by a parent (not on the bus) and still be Present here. Or a kid might ride the bus but then get picked up immediately — they'd still be Present here because they arrived.</p>
            </div>
            <Why>This is the official daily attendance record. The kitchen uses it to prepare lunches. The safety system uses it to know who is on the premises. If a kid is unmarked, nobody knows if they're here or not.</Why>
          </div>

          {/* 5 */}
          <div id="deadline-group" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Clock className="w-6 h-6 text-red-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">5. The 10:00 AM Deadline</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <Rule>Group attendance must be completed by 10:00 AM. After that, the system may lock your ability to mark.</Rule>
            <p className="text-base text-gray-700">The daily flow is:</p>
            <div className="space-y-2">
              <div className="p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg"><strong className="text-sm text-blue-900">Before 9:30 AM:</strong> <span className="text-sm text-blue-700">Bus attendance is being marked on the buses (rrdcbusmapping.com). Bus data locks at 9:30.</span></div>
              <div className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg"><strong className="text-sm text-indigo-900">9:30 AM - 10:00 AM:</strong> <span className="text-sm text-indigo-700">YOU mark your group attendance here (rrdcattendance.com → Summer 2026). You have until 10:00 AM.</span></div>
              <div className="p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg"><strong className="text-sm text-red-900">After 10:00 AM:</strong> <span className="text-sm text-red-700">Attendance locks. If conflicts exist between bus data and your marks, the supervisor resolves them.</span></div>
            </div>
          </div>

          {/* 6 */}
          <div id="front-desk" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Tablet className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">6. Front Desk — Late Arrivals & Early Dismissals</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <Rule>Counselors do NOT use the Front Desk section. This is for front desk staff ONLY.</Rule>
            <p className="text-base text-gray-700">The Front Desk section is a tablet at the camp entrance. When a parent drops off a camper late or picks one up early, the front desk staff uses this section to log it. You do NOT mark late arrivals or early dismissals.</p>
            <p className="text-base text-gray-700">What happens when the front desk marks a camper:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
              <li><strong>Absent (before camp / sick day):</strong> Camper is pulled off <strong>both</strong> the AM and PM bus rosters within ~30 seconds. The bus does not stop at their house.</li>
              <li><strong>Late arrival:</strong> The camper's status changes to "Late" in the system. This auto-resolves any bus conflict. You'll see them appear as Late on Camper Snapshot.</li>
              <li><strong>Early dismissal:</strong> The camper's status changes to "Dismissed" with the pickup time. You'll get an alert banner on your Camper Snapshot dashboard + an SMS if your phone number is on file.</li>
              <li><strong>Bus drivers' iPads:</strong> Within ~30 seconds, any camper marked <strong>Absent</strong>, <strong>Late</strong>, or <strong>Dismissed Early</strong> is automatically removed from the matching bus roster on the driver's iPad so the driver doesn't wait at an empty stop.</li>
            </ul>
            <p className="text-sm text-gray-500 mt-2">Front Desk login password: This is given to front desk staff, not counselors. If you need to know it, ask the camp director.</p>
          </div>

          {/* 7 */}
          <div id="bus-attendance" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><BusIcon className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">7. Bus Attendance (Read-Only View)</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The "Bus Attendance" card on the home screen shows a read-only view of the data coming from rrdcbusmapping.com (the Bus Mapping App). You cannot edit anything here. It's just a view for admin/front desk to see which kids were marked on each bus.</p>
            <p className="text-sm text-gray-500">You don't need to open this. Ever.</p>
          </div>

          {/* 8 */}
          <div id="how-it-connects" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><ClipboardCheck className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">8. How It All Connects to Camper Snapshot</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Three systems feed into Camper Snapshot's Conflict Match:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse my-2">
                <thead><tr className="bg-gray-100"><th className="border p-2 text-left">System</th><th className="border p-2 text-left">Who Uses It</th><th className="border p-2 text-left">Deadline</th><th className="border p-2 text-left">Field in Camper Snapshot</th></tr></thead>
                <tbody>
                  <tr><td className="border p-2 font-medium">Bus Mapping App</td><td className="border p-2">Bus counselor</td><td className="border p-2 font-bold text-red-700">9:30 AM</td><td className="border p-2">Bus App status</td></tr>
                  <tr><td className="border p-2 font-medium">Attendance App (Summer)</td><td className="border p-2">Group counselor (you)</td><td className="border p-2 font-bold text-red-700">10:00 AM</td><td className="border p-2">Attendance App status</td></tr>
                  <tr><td className="border p-2 font-medium">Front Desk</td><td className="border p-2">Front desk staff</td><td className="border p-2">Ongoing</td><td className="border p-2">Late / Dismissed status</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-base text-gray-700">If the Bus App and the Attendance App disagree (one says Present, the other says Absent), a <strong>red conflict</strong> appears in Camper Snapshot. The supervisor resolves it. You don't.</p>
          </div>

          {/* 9 */}
          <div id="troubleshooting" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><HelpCircle className="w-6 h-6 text-indigo-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">9. Something Not Working?</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Can't log in?</strong><p className="text-sm text-gray-600 mt-1">Make sure you're on Summer 2026, not Holiday Camp. Username = Group Code (any format works — b1, B1, b01, B01). Password = Group Name (e.g. Swordfish) — case-sensitive.</p></div>
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Camper missing from your list?</strong><p className="text-sm text-gray-600 mt-1">They probably aren't scheduled to be at camp today based on their session type (e.g. a Flex camper on a non-Flex day). Double-check the day's roster — if they really are at camp and still missing, tell your supervisor.</p></div>
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Marks not saving / past 10:00 AM?</strong><p className="text-sm text-gray-600 mt-1">If it's a connection issue, check your internet. If the system has locked, only <strong>Brian Stein</strong> can reopen it — find a supervisor with a radio and they'll call him.</p></div>
            </div>
          </div>

          <footer className="mt-12 pt-6 border-t text-center space-y-3 pb-8">
            <p className="text-sm font-bold text-indigo-900">Tech Support: Brian Stein</p>
            <p className="text-xs text-gray-400">Rolling River Day Camp — Attendance App Tutorial — rrdcattendance.com</p>
          </footer>
        </main>
      </div>

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 bg-indigo-700 text-white p-3 rounded-full shadow-lg hover:bg-indigo-600 z-50 print:hidden" data-testid="back-to-top"><ChevronUp className="w-5 h-5" /></button>
      )}
    </div>
  );
}
